/**
 * SOLID Review
 * S — Strong. One concern: the page load's half of the client error caps — the budget, the repeat window and the
 *     ledger of what was sent.
 * O — Strong. The budget, the window and the ledger's size are constants of the schema.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. The session part a decision reads and one move.
 * D — Strong. The caught error's value object, the schemas' types and the builder's part role.
 */
import type { ClientError } from './ClientError';
import type { IStateClientSessionPart } from '../decisions/ClientDecision';
import type { IPropsClientSessionChanges } from '../ports/schemas/ClientSession';
import {
  CLIENT_ERROR_SEND_BUDGET, CLIENT_ERROR_REPEAT_MS, CLIENT_ERROR_LEDGER_SIZE, type IEntityClientDiagnostics,
} from '../ports/schemas/DiagnosticsMoves';

/**
 * THE CLIENT'S HALF OF THE CAPS, AS A SMALL AGGREGATE (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the
 * decide function of `decisions/diagnostics/clientError.ts` (client error reporting, plan 2026-10-09, EP5): at most 20
 * reports per page load, and one error at most once a minute. The ledger keeps the 50 most recently sent keys.
 *
 * DEVIATION from the EP5 plan, flagged there: the plan counted repeats; counting them would write the client's state
 * on every repeat of an error that is already looping.
 */
export class ErrorLedger implements IStateClientSessionPart {
  private constructor(private ledger: IEntityClientDiagnostics) {}

  /** The ledger the session holds. */
  static of(diagnostics: IEntityClientDiagnostics): ErrorLedger {
    return new ErrorLedger(diagnostics);
  }

  /**
   * `error`, seen at `now`: sent and entered — or not, once the budget is spent or when the same error was sent
   * within the window.
   */
  sent(error: ClientError, now: number): boolean {
    if (this.ledger.sends >= CLIENT_ERROR_SEND_BUDGET) return false;
    const key = error.key();
    const last = this.ledger.lastSent.find(([sent]) => sent === key);
    if (last !== undefined && now - last[1] < CLIENT_ERROR_REPEAT_MS) return false;
    const kept = this.ledger.lastSent.filter(([sent]) => sent !== key);
    const lastSent: Array<[string, number]> = [...kept.slice(Math.max(0, kept.length - (CLIENT_ERROR_LEDGER_SIZE - 1))), [key, now]];
    this.ledger = { sends: this.ledger.sends + 1, lastSent };
    return true;
  }

  sessionFields(): IPropsClientSessionChanges {
    const fields: IPropsClientSessionChanges = { diagnostics: this.ledger };
    return fields;
  }
}
